/**
 * Contient les composants de Gauge Chart.
 *
 * Les gauge peuvent avoir une valeur maximal qui est peut être
 * une métrique à requêter.
 * En fonction de si la gauge est issue d'un dashboard, ou si on
 * est dans le requêteur, le hook a utilisé n'est pas le même.
 *
 * Pour cette raison, et comme React ne permet pas d'avoir des
 * hook appelé conditionnelement, on est obligé d'avoir 2 composants
 * (`GaugeWithExploreMaxValue` et `GaugeWithDashboardMaxValue`) qui
 * font la même chose, mais appele le hook correct pour leur type.
 *
 */

import React from 'react';
import ReactEcharts from 'echarts-for-react';
import { isChartKey } from 'routes/Explore/components/Sentence/helpers/chartKey';
import { useGetDashboardChartMaxValue } from 'components/ChartContainer/hooks/dashboardChart';
import { useGetExploreChartMaxValue } from 'components/ChartContainer/hooks/exploreChart';
import { useBackendSyncedContext } from 'routes/Explore/components/Contexts/BackendSyncedContext/BackendSyncedContext';
import { useChartContext } from 'components/ChartContainer/context/ChartContext';
import useGetDashboardFilters from 'components/QueryPerimeter/hooks/useGetDashboardFilters';
import { GraphProps } from '../types';
import {
    isNumberBroken,
    getGaugeMaxValue,
    getGaugeMinValue,
    addPercentageToNumberText,
} from './helpers/gaugeChart';
import { getColorsScale, getGaugeColorStops } from './helpers/getColor';
import { useGetDashboardFromSlugOrMetadata } from 'hooks/useGetDashboardFromSlugOrMetadata';
import { formatDisplay } from 'helpers/formatHelper';
import { getDecimalPrecision } from 'helpers/chart/settings';
import { ChartSettingsT } from 'business/definitions';

const getGaugeChartEchartsOption = ({
    numberText,
    scale,
    minValue,
    maxValue,
    isMaxValueLoading,
    value,
    yType,
    settings,
    colors,
}: {
    numberText: string;
    scale: number;
    minValue: number;
    maxValue: number;
    isMaxValueLoading?: boolean;
    value: number;
    yType: DataTypesT;
    settings: ChartSettingsT;
    colors: string[];
}) => {
    const valueLenght = (Math.round(value * 100) / 100).toString().length;
    const numberFontSize = scale / (valueLenght + 4);
    const numberTextFontSize = scale / 18;
    const tickFontSize = scale / 18;

    const isSmallGraph = scale > 300;
    // we adjust some params for small screens
    const gaugeWidth = isSmallGraph ? scale / 14 : scale / 10;

    const filledRatioRaw = (value - minValue) / (maxValue - minValue);

    const textColor = '#333333';
    const greyGradient = {
        type: 'linear',
        x: 0,
        y: 0,
        x2: 1,
        y2: 0,
        colorStops: [
            {
                offset: 0,
                color: '#ccc',
            },
            {
                offset: 1,
                color: '#bbb',
            },
        ],
        globalCoord: false,
    };

    const gradient = {
        type: 'linear',
        x: 0,
        y: 1,
        x2: 1,
        y2: 0,
        colorStops: getGaugeColorStops(filledRatioRaw, settings, colors),
        globalCoord: false,
    };

    const option = {
        textStyle: { fontFamily: 'Poppins' },
        series: [
            {
                name: numberText && `gauge-${numberText}`,
                type: 'gauge',
                min: isNumberBroken(minValue) ? 0 : minValue,
                max: isNumberBroken(maxValue) ? 0 : maxValue,
                splitNumber: 1,
                radius: '80%',
                progress: {
                    show: !Number.isNaN(filledRatioRaw),
                    width: gaugeWidth,
                    itemStyle: {
                        color: gradient,
                    },
                },
                axisLine: {
                    lineStyle: {
                        width: gaugeWidth,
                        color: [[1, greyGradient]],
                    },
                },
                axisTick: { show: false },
                pointer: { show: false },
                axisLabel: {
                    color: textColor,
                    fontSize: tickFontSize,
                    formatter(d: number) {
                        if (d === maxValue && isMaxValueLoading) {
                            return '...';
                        }
                        return formatDisplay(
                            d,
                            yType,
                            getDecimalPrecision(settings),
                            settings.displayBy,
                        );
                    },
                },
                splitLine: {
                    // Useful to have margin on the ticks
                    length: '-45%',
                    lineStyle: {
                        width: 0,
                    },
                },
                title: {
                    offsetCenter: [0, '25%'],
                    fontSize: numberTextFontSize,
                    lineHeight: scale / 15,
                    rich: {}, // needed for lineHeight to work
                },
                detail: {
                    color: textColor,
                    fontSize: numberFontSize,
                    offsetCenter: [0, '-10%'],
                    formatter(d: number) {
                        if (isNumberBroken(d)) {
                            return '-';
                        }
                        return formatDisplay(
                            d,
                            yType,
                            getDecimalPrecision(settings),
                            settings.displayBy,
                        );
                    },
                },
                data: [
                    {
                        value,
                        name: addPercentageToNumberText(
                            filledRatioRaw,
                            yType,
                            isNumberBroken(minValue) ? 0 : minValue,
                            isNumberBroken(maxValue) ? 0 : maxValue,
                            numberText,
                        ),
                    },
                ],
            },
        ],
    };
    return option;
};

type GaugeProps = GraphProps & {
    maxValue?: number | 'unknown';
    isMaxValueLoading?: boolean;
};

/**
 * Le "vrai" composant de gauge qui va rendre le chart ecahrt.
 */
const Gauge = ({
    settings,
    data,
    height,
    width,
    orderedColumns,
    onClick,
    maxValue,
    isMaxValueLoading,
}: GaugeProps) => {
    const colors = getColorsScale(settings?.palette);
    const yColumn = orderedColumns.y;

    const gaugeMaxValue = getGaugeMaxValue(
        settings.maxValue,
        maxValue,
        yColumn.type,
    );
    const minValue = getGaugeMinValue(settings.minValue, yColumn.type);
    const scale = Math.min(height, width);

    const numberText = settings.numberText ? settings.numberText : null;

    // @ts-expect-error [TS migration] Was not detected by flow
    const value = parseFloat(data[0][yColumn.key]);

    const onChartClick = React.useCallback(() => {
        onClick({});
    }, [onClick]);

    const params = {
        numberText: numberText || '',
        scale,
        minValue,
        maxValue: gaugeMaxValue,
        isMaxValueLoading,
        value,
        yType: yColumn.type,
        settings,
        colors,
    };

    const options = getGaugeChartEchartsOption(params);

    // This class is to allow playwright to detect when the gauge is loading
    const gaugeStatusClassName = isMaxValueLoading
        ? 'gauge-max-value-loading'
        : '';

    return (
        <ReactEcharts
            option={options}
            style={{ height, width }}
            className={`react_for_echarts ${gaugeStatusClassName}`}
            opts={{ renderer: 'canvas' }}
            onEvents={{ click: onChartClick }}
        />
    );
};

/**
 * Récupère la valeur maximum de la gauge (si nécessaire)
 * pour les chart de dashboard et passe la valeurs a `Gauge`.
 */
const GaugeWithDashboardMaxValue = ({
    chartKey,
    settings,
    orderedColumns,
    ...rest
}: GraphProps) => {
    const {
        queryFilters,
        queryMetrics,
        disableDashboardFilters,
        publicToken,
        isUpdateChartLoading,
    } = useChartContext();

    const dashboard = useGetDashboardFromSlugOrMetadata();

    const { data: dashboardFilters } = useGetDashboardFilters(
        dashboard,
        publicToken,
    );

    const { data: maxValue, isLoading: isMaxValueLoading } =
        useGetDashboardChartMaxValue({
            chartKey,
            metrics: queryMetrics,
            filters: queryFilters,
            maxValueField: settings.maxValueField,
            shouldTriggerMaxValueQuery: !isUpdateChartLoading,
            publicToken,
            disableDashboardFilters,
            dashboardFilters,
        });

    return (
        <Gauge
            chartKey={chartKey}
            settings={settings}
            orderedColumns={orderedColumns}
            maxValue={maxValue}
            isMaxValueLoading={isMaxValueLoading}
            {...rest}
        />
    );
};

/**
 * Récupère la valeur maximum de la gauge (si nécessaire)
 * pour les chart d'explore (i.e pas dans un dashboard)
 * et passe la valeurs a `Gauge`.
 */
const GaugeWithExploreMaxValue = ({
    chartKey,
    settings,
    orderedColumns,
    ...rest
}: GraphProps) => {
    const {
        queryFilters,
        queryMetrics,
        disableDashboardFilters,
        publicToken,
        isUpdateChartLoading,
    } = useChartContext();

    const dashboard = useGetDashboardFromSlugOrMetadata();

    const { data: dashboardFilters } = useGetDashboardFilters(
        dashboard,
        publicToken,
    );
    const { data: maxValue, isLoading: isMaxValueLoading } =
        useGetExploreChartMaxValue({
            chartKey,
            metrics: queryMetrics,
            filters: queryFilters,
            maxValueField: settings.maxValueField,
            shouldTriggerMaxValueQuery: !isUpdateChartLoading,
            publicToken,
            disableDashboardFilters,
            dashboardFilters,
        });

    return (
        <Gauge
            chartKey={chartKey}
            settings={settings}
            orderedColumns={orderedColumns}
            maxValue={maxValue}
            isMaxValueLoading={isMaxValueLoading}
            {...rest}
        />
    );
};

/**
 * Dans le cas ou la valeur maximum de la gauge est une métrique,
 * il faut fetch cette métrique.
 * En fonction du type de chart (dans un dashboard ou dans explore),
 * la méthode n'est pas la même.
 * Ce composant ne fait donc que retourner le bon composant de gauge
 * en fonction de si il a une valeur max et de son type.
 */
const GaugeWrapper = ({
    chartKey,
    isChartViewedOnDashboard,
    settings,
    isArgument,
    ...rest
}: GraphProps & { isArgument: boolean }) => {
    const { isBackendSynced } = useBackendSyncedContext();
    if (settings.maxValueField) {
        if (
            !isArgument &&
            (isChartViewedOnDashboard ||
                (isBackendSynced && isChartKey(chartKey)))
        ) {
            return (
                <GaugeWithDashboardMaxValue
                    settings={settings}
                    chartKey={chartKey}
                    {...rest}
                />
            );
        }
        return (
            <GaugeWithExploreMaxValue
                settings={settings}
                chartKey={chartKey}
                {...rest}
            />
        );
    }
    return <Gauge chartKey={chartKey} settings={settings} {...rest} />;
};

export default GaugeWrapper;
